/**
 * Copyright (c) 2018 itemis AG - All rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Contributors:
 * 	andreas muelder - itemis AG
 *
 */
import { util, shapes, ui } from "@clientio/rappid";
import { PreferenceStore } from "../services/preference-store";
import { LabeledNode, LabeledNodeController, LabeledNodeView } from "./base/labeled-node";
import { MARKER } from "./base/validation";

export class Exit extends LabeledNode {
  defaults() {
    return {
      ...super.defaults,
      type: "Exit",
      size: {
        height: 15,
        width: 15,
      },
      fixedRatio: true,
      embedable: false,
      linkable: true,
      attrs: {
        root: {
          magnetSelector: "body",
        },
        marker: {
          text: MARKER,
          fontFamily: "FontAwesome",
          fontSize: 13,
          x: -5,
          y: 0,
          opacity: 0,
        },
        body: {
          refCx: "50%",
          refCy: "50%",
          refR: "50%",
          strokeWidth: 0,
          fillOpacity: 1,
        },
        innerWhite: {
          refCx: "50%",
          refCy: "50%",
          refR: "43%",
          fillOpacity: 1,
        },
        horizontal: {
          // from edge to edge, sqrt(2) * 100 - refHeight = 131.42
          // remove length offset (must be smaller than 100). LengthOffset = 40
          refWidth: "91.42%", // 131.42 - lengthOffset = 91.42
          refHeight: "10%",
          // xOffset = sqrt((refHeight/2)² + (refHeight/2)²) = 7,07
          refX: "21.21%", // x = sqrt(lengthOffset²/2) + xOffset = 21.21
          refY: "14.14%", // x = sqrt(lengthOffset²/2) = 14.14
          transform: "rotate(45)",
        },
        orthogonal: {
          refWidth: "10%",
          refHeight: "91.42%",
          refX: "21.21%",
          refY: "85.86%",
          transform: "rotate(225)",
        },
      },
    };
  }

  getFillColor(): string {
    return this.attr(["innerWhite", "fill"]);
  }

  setFillColor(color: string | null) {
    this.attr(["innerWhite", "fill"], color);
    if (color === null) {
      this.removeAttr(["innerWhite", "fill"]);
    }
  }

  getBorderColor(): string {
    return this.attr(["body", "fill"]);
  }

  setBorderColor(color: string | null) {
    this.attr(["body", "fill"], color);
    if (color === null) {
      this.removeAttr(["body", "fill"]);
    }
  }

  getBorderWidth(): number {
    return this.attr(["body", "strokeWidth"]) || 0;
  }

  setBorderWidth(width: number) {
    this.attr(["body", "strokeWidth"], width);
  }

  resetDefaults() {
    this.setFillColor(null);
    this.setBorderColor(null);
    this.setBorderWidth(0);
  }

  markup = [
    {
      tagName: "circle",
      selector: "body",
    },
    {
      tagName: "circle",
      selector: "innerWhite",
    },
    {
      tagName: "rect",
      selector: "horizontal",
    },
    {
      tagName: "rect",
      selector: "orthogonal",
    },
    {
      tagName: "text",
      selector: "marker",
    },
  ];
}

export class ExitView extends LabeledNodeView {}

export class ExitController extends LabeledNodeController {
  getHaloConfig() {
    return [
      {
        name: "remove",
        position: ui.Halo.HandlePosition.NW,
        events: { pointerdown: "removeElement" },
        attrs: {
          ".handle": {
            "data-tooltip-class-name": "small",
            "data-tooltip": "Remove the object",
            "data-tooltip-position": "right",
            "data-tooltip-padding": 25,
          },
        },
      },
    ];
  }
}

util.assign(shapes, {
  Exit,
  ExitView,
  ExitController,
});
